import { cloneElement } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import {
  SLIDE_IN_MENUS,
  Z_INDEX_NAVBAR,
  Z_INDEX_SLIDE_IN_MENU,
} from "../../screens/root/constants";
import {
  BOX_RESIZE_DURATION_MS,
  SLIDE_IN_ANIMATION_DURATION_MS,
} from "./constants";
import { useSlideInMenuContainer } from "./hooks/useSlideInMenuContainer";
import { menus } from "./menus";

interface SlideInMenuCenteredProps {
  active: boolean;
  openMenu?: SLIDE_IN_MENUS;
  onCloseMenu: () => void;
}

/**
 * Centered slide-in menu for non-reader contexts.
 * Animates from bottom to top (translateY) and displays as a centered modal.
 */
export function SlideInMenuCentered({
  active,
  openMenu,
  onCloseMenu,
}: SlideInMenuCenteredProps) {
  const location = useLocation();
  const isMobile = useMobileBreakpoint();

  // Check for contexts that need higher z-index (above modals)
  const inViewAllSection = location.pathname.includes("cms/");
  const inChat = location.pathname.includes("chat/");
  const inAdminNext = location.pathname.includes("admin-next/");
  const needsHighZIndex = inViewAllSection || inChat || inAdminNext;

  // Only run container logic when active
  const containerProps = useSlideInMenuContainer({
    openMenu: active ? openMenu : undefined,
    onCloseMenu,
    enabled: active,
  });

  const {
    contentRef,
    scrollContainerRef,
    hasFunnel,
    delayedHasFunnel,
    contentHeight,
    onFunnelChange,
  } = containerProps;

  // When not active, hide instantly without animation
  if (!active) {
    return null;
  }

  const width = isMobile
    ? "100%"
    : delayedHasFunnel
      ? "566px"
      : "800px";

  const MARGIN = 20;
  const maxHeight = window.innerHeight - MARGIN * 2;
  const shouldUseDynamicHeight = hasFunnel && contentHeight;
  const height = isMobile
    ? "100dvh"
    : shouldUseDynamicHeight
      ? `${Math.min(contentHeight, maxHeight)}px`
      : `${maxHeight}px`;

  return (
    <Box
      id="slideInMenuCentered"
      position="fixed"
      inset="0"
      display="flex"
      alignItems="center"
      justifyContent="center"
      // Z-index: 2 for normal desktop contexts (so modals opened from the
      // slide-in render above it), Z_INDEX_SLIDE_IN_MENU for CMS/chat/admin
      // contexts (above modals), and above navbar on mobile (full-screen).
      zIndex={
        isMobile
          ? Z_INDEX_NAVBAR + 1
          : needsHighZIndex
            ? Z_INDEX_SLIDE_IN_MENU
            : 2
      }
      transform={openMenu ? "translateY(0)" : "translateY(350vh)"}
      transition={`transform ${SLIDE_IN_ANIMATION_DURATION_MS}ms ease-out`}
      pointerEvents="none"
      onClick={(e) => e.stopPropagation()}
    >
      <Box
        position="relative"
        margin={isMobile ? "0px" : "20px"}
        background="charcoal.charcoal"
        backdropFilter="blur(12px)"
        overflow="hidden"
        borderRadius={isMobile ? "0px" : "16px"}
        border="1px solid var(--White-Transparency-White-20, rgba(255,255,255,0.2))"
        width={width}
        maxWidth={isMobile ? undefined : "calc(100vw - 40px)"}
        height={height}
        sx={{
          transition: `width ${BOX_RESIZE_DURATION_MS}ms ease-in-out, height ${BOX_RESIZE_DURATION_MS}ms ease-in-out`,
        }}
      >
        <Flex
          id="slideInMenuScrollContainer"
          ref={scrollContainerRef}
          flexDirection="column"
          pointerEvents="auto"
          width="100%"
          height="100%"
        >
          <Box
            ref={contentRef}
            width="100%"
            minHeight={hasFunnel ? undefined : "100%"}
            maxHeight="calc(100vh - 20px)"
          >
            {openMenu
              ? cloneElement(menus[openMenu]!, {
                  onCloseMenu,
                  onFunnelChange,
                })
              : null}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}
